import { Suspense, useRef } from "react";
import HeroVideoBG from "@/components/HeroVideoBG";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { NarratedHeadline } from "@/components/Hero";

const HeroDotField = lazy(() => import("@/components/HeroDotField"));

/**
 * "Why Mane" video block — full-height cycling video with the dot
 * pattern overlay and the playable rotating manifesto headline that
 * used to live in the hero. Sits below the "Works where you are."
 * channel strip on the landing page.
 */
const WhyManeVideo = () => {
  const headlineRef = useRef<HTMLHeadingElement | null>(null);

  return (
    <section className="relative isolate overflow-hidden bg-background text-foreground">
      <div
        className="relative w-full min-h-[100svh] md:min-h-[110svh] lg:min-h-[120svh] flex items-center cursor-pointer"
        onClick={(e) => {
          const t = e.target as HTMLElement;
          if (t.closest("h1") || t.closest("button")) return;
          headlineRef.current?.click();
        }}
      >
        {/* Cycling video lifted out of the hero. */}
        <div className="absolute inset-0 z-0">
          <HeroVideoBG />
        </div>
        {/* Canvas dot field — same overlay used on /product hero. */}
        <Suspense fallback={null}>
          <HeroDotField forceLight className="pointer-events-none absolute inset-0 z-[2]" />
        </Suspense>
        {/* Dark scrim so the white overlay text stays legible. */}
        <div
          aria-hidden
          className="absolute inset-0 z-[3] pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.28) 40%, rgba(0,0,0,0.6) 100%)",
          }}
        />

        <div className="relative z-10 w-full max-w-content mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20 sm:pb-28 flex flex-col items-center text-center">
          <div className="pointer-events-auto w-full flex flex-col items-center text-center">
            <NarratedHeadline headlineRef={headlineRef} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyManeVideo;
